import axios, { AxiosInstance } from "axios";
import { getToken } from "./auth";
import { Envelope, EnvelopeStatus, Organization, QuickSendField, Recipient } from "../types";

const BASE_URL = "https://staging.api.ddsign.ae/api/v1";

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: BASE_URL,
  });

  client.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return client;
}

const client = createClient();

export async function getOrganizations(): Promise<Organization[]> {
  const response = await client.get("/organizations");
  return response.data.data;
}

export async function quickSend(
  orgId: string,
  file: Blob,
  fileName: string,
  recipients: Recipient[],
  options?: {
    name?: string;
    signingOrder?: string;
    emailSubject?: string;
    emailMessage?: string;
    fields?: QuickSendField[];
  }
): Promise<Envelope> {
  const formData = new FormData();
  formData.append("file", file, fileName);

  recipients.forEach((r, i) => {
    formData.append(`recipients[${i}][name]`, r.name);
    formData.append(`recipients[${i}][email]`, r.email);
    formData.append(`recipients[${i}][role]`, r.role);
  });

  if (options?.name) formData.append("name", options.name);
  if (options?.signingOrder) formData.append("signing_order", options.signingOrder);
  if (options?.emailSubject) formData.append("email_subject", options.emailSubject);
  if (options?.emailMessage) formData.append("email_message", options.emailMessage);

  if (options?.fields) {
    options.fields.forEach((f, i) => {
      formData.append(`fields[${i}][name]`, f.name);
      formData.append(`fields[${i}][type]`, f.type);
      formData.append(`fields[${i}][recipient_index]`, String(f.recipient_index));
      formData.append(`fields[${i}][page_number]`, String(f.page_number));
      formData.append(`fields[${i}][x_position]`, String(f.x_position));
      formData.append(`fields[${i}][y_position]`, String(f.y_position));
      formData.append(`fields[${i}][width]`, String(f.width));
      formData.append(`fields[${i}][height]`, String(f.height));
      formData.append(`fields[${i}][is_required]`, f.is_required ? "1" : "0");
    });
  }

  const response = await client.post(`/organizations/${orgId}/envelopes/quick-send`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data.data;
}

export async function getEnvelopeStatus(orgId: string, envelopeId: string): Promise<EnvelopeStatus> {
  const response = await client.get(`/organizations/${orgId}/envelopes/${envelopeId}/status`);
  return response.data.data;
}
